import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CziConversionStatus, FileDatasetValidationStatus } from '@ca/core/models';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { FileSegmentationValidationStatus } from 'src/app/core/types/file-segmentation-validation-statuses.type';

@UntilDestroy()
@Component({
    selector: 'ca-file-status-icon',
    templateUrl: './file-status-icon.component.html',
    styleUrls: ['./file-status-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileStatusIconComponent {
    @Input({ required: true }) convertStatus: CziConversionStatus = 'Unknown';
    @Input({ required: true }) validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus = 'Unknown';
    @Input() shouldShowValidationStatus: boolean = false;
    @Input() isSegmentation?: boolean;

    constructor(private translateService: TranslateService) {}

    /**
     * Provides the tooltip message based on the current conversion status.
     * @returns The tooltip message for the icon.
     */
    get conversionTooltip(): string {
        switch (this.convertStatus) {
            case 'NotCZI':
                return this.translateService.instant('CZI_CONVERSION.FILE_STATUS.NOT_CZI_IN_SEGMENT');
            case 'Active':
                return this.translateService.instant('CZI_CONVERSION.FILE_STATUS.CONVERTING_TO_CZI');
            case 'Failed':
                return this.translateService.instant('CZI_CONVERSION.FILE_STATUS.FAILED_IN_SEGMENT');
            case 'Success':
                return this.translateService.instant('CZI_CONVERSION.FILE_STATUS.SUCCESS');
            default:
                return this.translateService.instant('CZI_CONVERSION.FILE_STATUS.UNKNOWN_STATUS');
        }
    }

    /**
     * Provides the tooltip message based on the current validation status.
     * @returns The tooltip message for the validation icon.
     */
    get validationTooltip(): string {
        switch (this.validationStatus) {
            case 'Valid':
                return this.translateService.instant('FILE_VALIDATION.VALID');
            case 'ChannelStructureMismatch':
                return this.isSegmentation
                    ? this.translateService.instant('FILE_VALIDATION.CHANNEL_MISMATCH_SEGMENTATION')
                    : this.translateService.instant('FILE_VALIDATION.CHANNEL_STRUCTURE_MISMATCH');
            case 'InvalidFileFormat':
                return this.translateService.instant('FILE_VALIDATION.INVALID_FILE_FORMAT');
            case 'InvalidPixelType':
                return this.translateService.instant('FILE_VALIDATION.INVALID_PIXEL_TYPE');
            case 'InvalidBitDepth':
                return this.translateService.instant('FILE_VALIDATION.INVALID_BIT_TYPE');
            case 'UnsupportedDimensions':
                return this.translateService.instant('FILE_VALIDATION.UNSUPPORTED_DIMENSIONS');
            case 'XYDimensionsTooSmall':
                return this.isSegmentation
                    ? this.translateService.instant('FILE_VALIDATION.XY_DIMENSIONS_NOT_SUPPORTED_FOR_SEGMENTATION')
                    : this.translateService.instant('FILE_VALIDATION.XY_DIMENSIONS_TOO_SMALL');
            case 'MetadataAbsent':
                return this.translateService.instant('FILE_VALIDATION.UNREADABLE_CZI');
            case 'Unknown':
                return this.translateService.instant('FILE_VALIDATION.UNKNOWN_VALIDATION');
            case 'InvalidWithoutReason':
                return this.translateService.instant('FILE_VALIDATION.INVALID');
            case 'PendingValidation':
                return this.translateService.instant('FILE_VALIDATION.VALIDATING');
            case 'InvalidAmlModelVersion':
                return this.translateService.instant('FILE_VALIDATION.INVALID_AML_MODEL_VERSION"');
            default:
                return this.translateService.instant('FILE_VALIDATION.UNKNOWN_STATUS');
        }
    }

    /**
     * Returns the icon name based on the current conversion status.
     * @returns The name of the icon to display.
     */
    get iconName(): string {
        switch (this.convertStatus) {
            case 'NotCZI':
            case 'Failed':
                return 'error_outline';
            case 'Active':
                return 'loading';
            case 'Success':
                return 'checkmark';
            default:
                return 'help';
        }
    }

    /**
     * Returns the icon color class based on the current status.
     * Loading icons have no color class.
     * @returns The color class to apply to the icon, or an empty string for default styling.
     */
    get iconColorClass(): string {
        return this.iconName === 'loading'
            ? ''
            : this.convertStatus === 'Success' && this.validationStatus === 'Valid'
              ? 'ca-color-success'
              : 'ca-color-icon-error';
    }
    /**
     * Returns the margin class based on whether validation status should be shown.
     * @returns The margin class for the icon.
     */
    get marginClass(): string {
        return this.shouldShowValidationStatus || this.isSegmentation ? 'me-3' : 'me-0';
    }
}
